import { getApiPrefix } from '../utils/apiHelper';
import React, { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DateInput } from "../components/ui/date-input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Label } from "../components/ui/label";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { Upload, Trash2, Loader2, ArrowUpCircle, ArrowDownCircle, Banknote, Plus, Layers, ChevronLeft, ChevronRight, Search, Pencil, X, Wallet, Download } from "lucide-react";
import { useFormValidation } from "../hooks/useFormValidation";
import { InputWithError } from "../components/common/InputWithError";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { SortableHeader, sortData } from "../components/ui/sortable-header";
import { Checkbox } from "../components/ui/checkbox";
import { ExcelUploadHelper, EXCEL_COLUMNS } from "../components/ui/excel-upload-helper";
import { useTranslation } from "../contexts/I18nContext";
import { ManualMovementDialog, AllocationDialog, DeleteMovementDialogs } from "./BankMovementsDialogs";

const ITEMS_PER_PAGE = 50;

export default function BankMovementsPage() {
  const { t } = useTranslation();
  const [movements, setMovements] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [appartamenti, setAppartamenti] = useState([]);
  const [banche, setBanche] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Per giroconto
  const [allBankMovements, setAllBankMovements] = useState([]);
  const [cardMovements, setCardMovements] = useState([]);
  
  // Pagination & Sort (client-side)
  const [currentPage, setCurrentPage] = useState(1);
  const [sort, setSort] = useState({ field: "data", direction: "desc" });
  
  // Filters (client-side)
  const [filterStato, setFilterStato] = useState("");
  const [filterTipo, setFilterTipo] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchInput, setSearchInput] = useState("");
  
  const [uploading, setUploading] = useState(false);
  const [manualDialog, setManualDialog] = useState(false);
  const [manualForm, setManualForm] = useState({ data: "", data_valuta: "", descrizione: "", importo: "", tipo: "uscita", banca_id: "" });
  const [savingManual, setSavingManual] = useState(false);
  const [savingAllocation, setSavingAllocation] = useState(false);
  const [deletingMovement, setDeletingMovement] = useState(null);
  const [deletingAllocation, setDeletingAllocation] = useState(null);
  
  // Export Excel per periodo
  const [exportDialog, setExportDialog] = useState(false);
  const [exportForm, setExportForm] = useState({ data_da: "", data_a: "", banca_id: "__all__" });
  const [exporting, setExporting] = useState(false);
  
  // Selezione multipla
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deletingBulk, setDeletingBulk] = useState(false);
  const [deleteMultipleDialog, setDeleteMultipleDialog] = useState(false);
  const [deleteAllDialog, setDeleteAllDialog] = useState(false);
  
  const [allocDialog, setAllocDialog] = useState({ open: false, movement: null, allocations: [] });
  const [allocType, setAllocType] = useState("fattura");
  const [allocForm, setAllocForm] = useState({ invoice_id: "", account_id: "", cost_center_id: "", importo: "", note: "", tipo_allocazione: "standard", giroconto_bank_movement_id: "", giroconto_card_movement_id: "", data_inizio_competenza: "", data_fine_competenza: "", stipendi_mese_riferimento: "", affitto_appartamento_id: "", affitto_mese_riferimento: "" });
  const [editingAllocationId, setEditingAllocationId] = useState(null); // ID allocazione in modifica
  const [stipendiMesi, setStipendiMesi] = useState([]); // Mesi disponibili per allocazione stipendi
  const [affittiAllocabili, setAffittiAllocabili] = useState([]); // Affitti allocabili per (app,mese)
  const [pianiScadenzeRicorrenti, setPianiScadenzeRicorrenti] = useState([]); // Piani con rate allocabili
  const [bolletteManualiAperte, setBolletteManualiAperte] = useState([]); // Bollette manuali non pagate

  // Stats (calcolate separatamente)
  const [stats, setStats] = useState({ daAllocare: 0 });

  // Validazione form movimento bancario
  const { errors: manualFormErrors, validate: validateManualForm, validateField: validateManualField, clearErrors: clearManualFormErrors } = useFormValidation({
    data: { required: true },
    descrizione: { required: true },
    importo: { required: true, number: true }
  });

  const [allocAuxLoaded, setAllocAuxLoaded] = useState(false);

  const fetchMovements = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${getApiPrefix()}/bank-movements?page_size=5000`);
      setMovements(res.data.items || []);
    } catch (err) {
      handleApiError(err, t("errors.loadMovements"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchAuxData = useCallback(async () => {
    try {
      const [accRes, ccRes, bancheRes] = await Promise.all([
        api.get(`${getApiPrefix()}/accounts`),
        api.get(`${getApiPrefix()}/cost-centers`),
        api.get(`${getApiPrefix()}/banche`),
      ]);
      setAccounts(accRes.data);
      setCostCenters(ccRes.data);
      setBanche(bancheRes.data || []);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error("fetchAncillary error", err);
    }
  }, []);

  const fetchAllocAuxData = useCallback(async () => {
    if (allocAuxLoaded) return;
    try {
      // Endpoint /invoices/allocabili è ottimizzato (solo campi minimi, esclude fatture già allocate).
      // I movimenti bancari/carta vengono caricati SOLO quando l'utente sceglie il tipo giroconto (lazy).
      const [invRes, appRes, bmAperteRes] = await Promise.all([
        api.get(`${getApiPrefix()}/invoices/allocabili`),
        api.get(`${getApiPrefix()}/appartamenti`).catch(() => ({ data: [] })),
        api.get(`${getApiPrefix()}/bollette-manuali/aperte`).catch(() => ({ data: [] })),
      ]);
      setInvoices(invRes.data || []);
      setAppartamenti(appRes.data || []);
      setBolletteManualiAperte(bmAperteRes.data || []);
      setAllocAuxLoaded(true);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error("fetchAllocAuxData error", err);
    }
  }, [allocAuxLoaded]);

  // Lazy-load movimenti bancari/carta solo quando serve (giroconti)
  const fetchGirocontoLists = useCallback(async () => {
    if (allBankMovements.length > 0 && cardMovements.length > 0) return;
    try {
      const [allBankRes, cardRes] = await Promise.all([
        api.get(`${getApiPrefix()}/bank-movements?page_size=5000`),
        api.get(`${getApiPrefix()}/card-movements?page_size=5000`),
      ]);
      setAllBankMovements(allBankRes.data.items || []);
      setCardMovements(cardRes.data.items || []);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error("fetchGirocontoLists error", err);
    }
  }, [allBankMovements.length, cardMovements.length]);

  const fetchStats = useCallback(async () => {
    try {
      const daAllocareRes = await api.get(`${getApiPrefix()}/bank-movements?stato=da_allocare&page_size=1`);
      setStats(prev => ({ ...prev, daAllocare: daAllocareRes.data.total || 0 }));
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error("fetchStats error", err);
    }
  }, []);

  // Calcola stats YTD (da inizio anno) dai movimenti caricati
  const currentYear = new Date().getFullYear();
  const ytdStats = React.useMemo(() => {
    let entrate = 0, uscite = 0;
    movements.forEach(m => {
      // Determina anno dal formato data (dd-mm-yyyy o yyyy-mm-dd)
      let year = null;
      if (m.data) {
        const parts = m.data.split("-");
        if (parts[0].length === 4) year = parseInt(parts[0]);
        else if (parts[2] && parts[2].length === 4) year = parseInt(parts[2]);
      }
      if (year === currentYear) {
        if (m.importo > 0) entrate += m.importo;
        else uscite += Math.abs(m.importo);
      }
    });
    return { entrate, uscite };
  }, [movements, currentYear]);

  // Calcola disponibilità liquide per banca (saldo_iniziale + somma movimenti)
  const liquidita = React.useMemo(() => {
    const bancheMap = {};
    banche.forEach(b => {
      bancheMap[b.id] = { nome: b.nome, saldo_iniziale: b.saldo_iniziale || 0, movimenti: 0 };
    });
    movements.forEach(m => {
      if (m.banca_id && bancheMap[m.banca_id]) {
        bancheMap[m.banca_id].movimenti += (m.importo || 0);
      }
    });
    const list = Object.values(bancheMap).map(b => ({
      nome: b.nome,
      saldo_iniziale: b.saldo_iniziale,
      movimenti: b.movimenti,
      saldo_attuale: b.saldo_iniziale + b.movimenti
    }));
    const totale = list.reduce((sum, b) => sum + b.saldo_attuale, 0);
    return { banche: list, totale };
  }, [movements, banche]);

  useEffect(() => { fetchMovements(); }, [fetchMovements]);
  useEffect(() => { fetchAuxData(); fetchStats(); }, [fetchAuxData, fetchStats]);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [filterStato, filterTipo, searchText]);

  // Client-side filtering
  const filteredMovements = movements.filter(m => {
    const matchStato = !filterStato || filterStato === "all" || m.stato_allocazione === filterStato;
    const matchTipo = !filterTipo || filterTipo === "all" || m.tipo === filterTipo;
    const matchSearch = !searchText || (m.descrizione || "").toLowerCase().includes(searchText.toLowerCase());
    return matchStato && matchTipo && matchSearch;
  });

  // Client-side sort and paginate
  const sortedMovements = sortData(filteredMovements, sort);
  const totalPages = Math.ceil(sortedMovements.length / ITEMS_PER_PAGE);
  const paginatedMovements = sortedMovements.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleSearch = () => {
    setSearchText(searchInput);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post(`${getApiPrefix()}/bank-movements/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      showSuccess(res.data.message);
      fetchMovements();
      fetchStats();
    } catch (err) {
      if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        handleApiError({ message: "Il caricamento sta impiegando troppo tempo. Attendi qualche istante e ricarica la pagina per verificare se i dati sono stati importati. Non riprovare il caricamento per evitare duplicati." });
      } else {
        handleApiError(err, t("errors.errorUpload"));
      }
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (exportForm.data_da) params.set("data_da", exportForm.data_da);
      if (exportForm.data_a) params.set("data_a", exportForm.data_a);
      if (exportForm.banca_id && exportForm.banca_id !== "__all__") params.set("banca_id", exportForm.banca_id);
      const res = await api.get(`${getApiPrefix()}/bank-movements/export?${params}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      let filename = "movimenti_bancari";
      if (exportForm.data_da || exportForm.data_a) {
        filename += `_${exportForm.data_da || 'inizio'}_${exportForm.data_a || 'oggi'}`;
      }
      link.setAttribute("download", `${filename}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccess("Excel scaricato");
      setExportDialog(false);
    } catch (err) {
      handleApiError(err, "Errore durante il download");
    } finally {
      setExporting(false);
    }
  };

  const handleManualCreate = async () => {
    // Validazione client-side
    if (!validateManualForm(manualForm)) {
      return;
    }
    setSavingManual(true);
    try {
      const params = new URLSearchParams({
        data: manualForm.data,
        descrizione: manualForm.descrizione,
        importo: manualForm.importo,
        tipo: manualForm.tipo
      });
      if (manualForm.data_valuta) params.append("data_valuta", manualForm.data_valuta);
      if (manualForm.banca_id) params.append("banca_id", manualForm.banca_id);
      await api.post(`${getApiPrefix()}/bank-movements?${params.toString()}`);
      showSuccess(t("success.movementCreated"));
      setManualDialog(false);
      setManualForm({ data: "", data_valuta: "", descrizione: "", importo: "", tipo: "uscita", banca_id: "" });
      clearManualFormErrors();
      fetchMovements();
      fetchStats();
    } catch (err) {
      handleApiError(err, t("errors.errorCreation"));
    } finally {
      setSavingManual(false);
    }
  };

  const [deleteSingleDialog, setDeleteSingleDialog] = useState(null);

  const handleDelete = async (id) => {
    setDeletingMovement(id);
    try {
      await api.delete(`${getApiPrefix()}/bank-movements/${id}`);
      showSuccess(t("success.movementDeleted"));
      setDeleteSingleDialog(null);
      fetchMovements();
      fetchStats();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    } finally {
      setDeletingMovement(null);
    }
  };

  const handleBulkDelete = async () => {
    const count = selectedIds.size;
    setDeletingBulk(true);
    try {
      await api.post(`${getApiPrefix()}/bank-movements/bulk-delete`, { ids: Array.from(selectedIds) });
      showSuccess(`${count} movimenti eliminati`);
      setSelectedIds(new Set());
      setDeleteMultipleDialog(false);
      fetchMovements();
      fetchStats();
    } catch (err) {
      handleApiError(err, t("errors.errorMultiDelete"));
    } finally {
      setDeletingBulk(false);
    }
  };

  const handleDeleteAll = async () => {
    setDeletingBulk(true);
    try {
      await api.delete(`${getApiPrefix()}/bank-movements`);
      showSuccess(t("success.allBankMovementsDeleted"));
      setSelectedIds(new Set());
      setDeleteAllDialog(false);
      fetchMovements();
      fetchStats();
    } catch (err) {
      handleApiError(err, t("errors.errorDeleteTotal"));
    } finally {
      setDeletingBulk(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === sortedMovements.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedMovements.map(m => m.id)));
    }
  };

  const openAllocDialog = async (movement) => {
    try {
      const [allocRes, stipendiRes, affittiRes, pianiRes] = await Promise.all([
        api.get(`${getApiPrefix()}/bank-movements/${movement.id}/allocations`),
        api.get(`${getApiPrefix()}/personnel-costs/mesi-allocabili`).catch(() => ({ data: [] })),
        api.get(`${getApiPrefix()}/appartamenti/affitti-allocabili`).catch(() => ({ data: [] })),
        api.get(`${getApiPrefix()}/scheduled-payments/rate-allocabili`).catch(() => ({ data: [] })),
        fetchAllocAuxData(),
      ]);
      setAllocDialog({ open: true, movement, allocations: allocRes.data });
      setStipendiMesi(stipendiRes.data || []);
      setAffittiAllocabili(affittiRes.data || []);
      setPianiScadenzeRicorrenti(pianiRes.data || []);
      const totaleAllocato = allocRes.data.reduce((sum, a) => sum + (a.importo || 0), 0);
      const residuo = Math.abs(movement.importo) - totaleAllocato;
      setAllocForm({ invoice_id: "", account_id: "", cost_center_id: "", importo: String(Math.max(0, residuo).toFixed(2)), note: "", tipo_allocazione: "standard", giroconto_bank_movement_id: "", giroconto_card_movement_id: "", data_inizio_competenza: "", data_fine_competenza: "", stipendi_mese_riferimento: "", affitto_appartamento_id: "", affitto_mese_riferimento: "", piano_ricorrente_key: "", scheduled_payment_id: "" });
      setEditingAllocationId(null);
    } catch (err) {
      handleApiError(err, t("errors.loadAllocations"));
    }
  };

  // Calcola residuo da allocare (escludendo l'allocazione in modifica)
  const calcolaResiduo = () => {
    if (!allocDialog.movement) return 0;
    const totaleAllocato = allocDialog.allocations
      .filter(a => a.id !== editingAllocationId)
      .reduce((sum, a) => sum + (a.importo || 0), 0);
    return Math.abs(allocDialog.movement.importo) - totaleAllocato;
  };

  // Avvia modifica allocazione
  const startEditAllocation = (alloc) => {
    setEditingAllocationId(alloc.id);
    
    // Determina il tipo di allocazione
    let tipo = "conto";
    if (alloc.invoice_id) tipo = "fattura";
    else if (alloc.giroconto_bank_movement_id || alloc.giroconto_card_movement_id) tipo = "giroconto";
    else if (alloc.tipo_allocazione === "assicurazioni_veicoli") tipo = "assicurazione";
    else if (alloc.tipo_allocazione === "bollo_veicoli") tipo = "bollo_auto";
    else if (alloc.tipo_allocazione === "carburanti") tipo = "carburante";
    else if (alloc.tipo_allocazione === "bollette") tipo = "bolletta";
    else if (alloc.tipo_allocazione === "manutenzioni_appartamenti") tipo = "manutenzione_appartamento";
    else if (alloc.tipo_allocazione === "stipendi") tipo = "stipendi";
    else if (alloc.tipo_allocazione === "affitti") tipo = "affitti";
    else if (alloc.tipo_allocazione === "scadenze_ricorrenti") tipo = "scadenze_ricorrenti";
    
    setAllocType(tipo);
    setAllocForm({
      invoice_id: alloc.invoice_id || "",
      account_id: alloc.account_id || "",
      cost_center_id: alloc.cost_center_id || "",
      importo: String(alloc.importo || ""),
      note: alloc.note || "",
      tipo_allocazione: alloc.tipo_allocazione || "standard",
      giroconto_bank_movement_id: alloc.giroconto_bank_movement_id || "",
      giroconto_card_movement_id: alloc.giroconto_card_movement_id || "",
      data_inizio_competenza: alloc.data_inizio_competenza || "",
      data_fine_competenza: alloc.data_fine_competenza || "",
      stipendi_mese_riferimento: alloc.stipendi_mese_riferimento || "",
      affitto_appartamento_id: "",
      affitto_mese_riferimento: alloc.affitto_mese_riferimento || "",
      piano_ricorrente_key: "",
      scheduled_payment_id: alloc.scheduled_payment_id || ""
    });
  };

  // Annulla modifica
  const cancelEditAllocation = () => {
    setEditingAllocationId(null);
    const residuo = calcolaResiduo();
    setAllocForm({ invoice_id: "", account_id: "", cost_center_id: "", importo: String(residuo.toFixed(2)), note: "", tipo_allocazione: "standard", giroconto_bank_movement_id: "", giroconto_card_movement_id: "", data_inizio_competenza: "", data_fine_competenza: "", stipendi_mese_riferimento: "", affitto_appartamento_id: "", affitto_mese_riferimento: "" });
    setAllocType("fattura");
  };

  const handleAddAllocation = async () => {
    const { movement } = allocDialog;
    if (!movement) return;
    
    const payload = {
      importo: parseFloat(allocForm.importo),
      tipo_allocazione: allocForm.tipo_allocazione,
      note: allocForm.note || null,
      ...(allocType === "fattura" && allocForm.invoice_id ? { invoice_id: allocForm.invoice_id } : {}),
      ...((allocType === "conto" || allocType === "assicurazione" || allocType === "bollo_auto") && allocForm.account_id ? { account_id: allocForm.account_id } : {}),
      ...(allocType === "giroconto" && allocForm.giroconto_bank_movement_id ? { giroconto_bank_movement_id: allocForm.giroconto_bank_movement_id } : {}),
      ...(allocType === "giroconto" && allocForm.giroconto_card_movement_id ? { giroconto_card_movement_id: allocForm.giroconto_card_movement_id } : {}),
      ...(allocForm.cost_center_id ? { centro_costo_id: allocForm.cost_center_id } : {}),
      ...(allocType === "conto" && allocForm.data_inizio_competenza ? { data_inizio_competenza: allocForm.data_inizio_competenza } : {}),
      ...(allocType === "conto" && allocForm.data_fine_competenza ? { data_fine_competenza: allocForm.data_fine_competenza } : {}),
      ...(allocType === "stipendi" && allocForm.stipendi_mese_riferimento ? { stipendi_mese_riferimento: allocForm.stipendi_mese_riferimento } : {}),
      ...(allocType === "affitti" && allocForm.affitto_mese_riferimento ? { affitto_mese_riferimento: allocForm.affitto_mese_riferimento } : {}),
      ...(allocType === "scadenze_ricorrenti" && allocForm.scheduled_payment_id ? { scheduled_payment_id: allocForm.scheduled_payment_id } : {}),
      ...(allocType === "bolletta" && allocForm.bolletta_manuale_id ? { bolletta_manuale_id: allocForm.bolletta_manuale_id } : {})
    };
    
    // Imposta tipo_allocazione in base al tipo selezionato
    if (allocType === "giroconto") {
      payload.tipo_allocazione = "giroconto";
    } else if (allocType === "assicurazione") {
      payload.tipo_allocazione = "assicurazioni_veicoli";
    } else if (allocType === "bollo_auto") {
      payload.tipo_allocazione = "bollo_veicoli";
    } else if (allocType === "carburante") {
      payload.tipo_allocazione = "carburanti";
    } else if (allocType === "bolletta") {
      payload.tipo_allocazione = "bollette";
    } else if (allocType === "manutenzione_appartamento") {
      payload.tipo_allocazione = "manutenzioni_appartamenti";
    } else if (allocType === "stipendi") {
      payload.tipo_allocazione = "stipendi";
    } else if (allocType === "affitti") {
      payload.tipo_allocazione = "affitti";
    } else if (allocType === "scadenze_ricorrenti") {
      payload.tipo_allocazione = "scadenze_ricorrenti";
    }
    
    try {
      if (editingAllocationId) {
        // Modifica allocazione esistente
        await api.put(`${getApiPrefix()}/bank-movements/${movement.id}/allocations/${editingAllocationId}`, payload);
        showSuccess(t("success.allocationModified"));
      } else {
        // Nuova allocazione
        await api.post(`${getApiPrefix()}/bank-movements/${movement.id}/allocations`, payload);
        showSuccess(t("success.allocationAdded"));
      }
      const res = await api.get(`${getApiPrefix()}/bank-movements/${movement.id}/allocations`);
      setAllocDialog({ ...allocDialog, allocations: res.data });
      // Aggiorna mesi stipendi se appena allocato uno stipendio
      if (allocType === "stipendi") {
        try {
          const stipRes = await api.get(`${getApiPrefix()}/personnel-costs/mesi-allocabili`);
          setStipendiMesi(stipRes.data || []);
        } catch { /* ignore */ }
      }
      // Aggiorna affitti allocabili se appena allocato un affitto
      if (allocType === "affitti") {
        try {
          const affRes = await api.get(`${getApiPrefix()}/appartamenti/affitti-allocabili`);
          setAffittiAllocabili(affRes.data || []);
        } catch { /* ignore */ }
      }
      setAllocForm({ invoice_id: "", account_id: "", cost_center_id: "", importo: "", note: "", tipo_allocazione: "standard", giroconto_bank_movement_id: "", giroconto_card_movement_id: "", data_inizio_competenza: "", data_fine_competenza: "", stipendi_mese_riferimento: "", affitto_appartamento_id: "", affitto_mese_riferimento: "" });
      setEditingAllocationId(null);
      fetchMovements();
      fetchStats();
    } catch (err) {
      handleApiError(err, t("errors.errorAllocation"));
    }
  };

  const handleDeleteAllocation = async (allocId) => {
    const { movement } = allocDialog;
    if (!movement) return;
    try {
      await api.delete(`${getApiPrefix()}/bank-movements/${movement.id}/allocations/${allocId}`);
      showSuccess(t("success.allocationDeleted"));
      const res = await api.get(`${getApiPrefix()}/bank-movements/${movement.id}/allocations`);
      setAllocDialog({ ...allocDialog, allocations: res.data });
      fetchMovements();
      fetchStats();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    }
  };

  const formatCurrency = (val) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val || 0);

  const getTipoBadge = (tipo) => {
    if (tipo === "entrata") return <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">{t('dialogs.income')}</Badge>;
    return <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">{t('dialogs.expense')}</Badge>;
  };

  const getStatoBadge = (stato) => {
    const styles = {
      da_allocare: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
      parziale: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
      in_allocazione: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
      allocato: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
    };
    const labels = { da_allocare: "Da Allocare", parziale: "Parziale", in_allocazione: "In Allocazione", allocato: "Allocato" };
    return <Badge className={styles[stato] || ""}>{labels[stato] || stato}</Badge>;
  };

  const formatDate = (d) => {
    if (!d) return "-";
    if (d.includes("-") && d.split("-")[0].length === 4) {
      const [y,m,dd] = d.split("-");
      return `${dd}/${m}/${y}`;
    }
    return d;
  };

  return (
    <div className="space-y-6" data-testid="bank-movements-page">
      <div className="flex items-center gap-3">
        <Banknote className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("movements.title")}</h1>
          <p className="text-muted-foreground text-sm">Gestione e allocazione movimenti bancari</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="border-l-4 border-l-emerald-500 bg-gradient-to-r from-emerald-50/50 to-transparent dark:from-emerald-950/20 hover:shadow-md transition-shadow">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Entrate {currentYear}</p>
                <p className="text-2xl font-bold font-mono text-emerald-600 mt-1">{formatCurrency(ytdStats.entrate)}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <ArrowDownCircle className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500 bg-gradient-to-r from-red-50/50 to-transparent dark:from-red-950/20 hover:shadow-md transition-shadow">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wider">Uscite {currentYear}</p>
                <p className="text-2xl font-bold font-mono text-red-600 mt-1">{formatCurrency(ytdStats.uscite)}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <ArrowUpCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500 bg-gradient-to-r from-amber-50/50 to-transparent dark:from-amber-950/20 hover:shadow-md transition-shadow">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider">{t('common.toAllocate')}</p>
                <p className="text-2xl font-bold font-mono mt-1">{stats.daAllocare}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Layers className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Disponibilita Liquide */}
      {banche.length > 0 && (
        <Card className="border-l-4 border-l-blue-500 hover:shadow-md transition-shadow" data-testid="liquidita-card">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-4">
              <Wallet className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold text-sm uppercase tracking-wider text-blue-600 dark:text-blue-400">Disponibilita Liquide</h3>
              <span className="ml-auto text-2xl font-bold font-mono text-blue-700 dark:text-blue-300" data-testid="liquidita-totale">{formatCurrency(liquidita.totale)}</span>
            </div>
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {liquidita.banche.map((b, idx) => (
                <div key={b.id || `banca-${b.nome}`} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800">
                  <div>
                    <p className="font-medium text-sm">{b.nome}</p>
                    <p className="text-xs text-muted-foreground">Iniziale: {formatCurrency(b.saldo_iniziale)}</p>
                  </div>
                  <p className={`font-bold font-mono text-sm ${b.saldo_attuale >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid={`saldo-banca-${idx}`}>
                    {formatCurrency(b.saldo_attuale)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters + Toolbar */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-4">
            <Select value={filterStato} onValueChange={setFilterStato}>
              <SelectTrigger className="w-40"><SelectValue placeholder={t("common.allStatuses")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                <SelectItem value="da_allocare">{t('common.toAllocate')}</SelectItem>
                <SelectItem value="parziale">{t('common.partial')}</SelectItem>
                <SelectItem value="allocato">{t('invoices.allocated')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterTipo} onValueChange={setFilterTipo}>
              <SelectTrigger className="w-40"><SelectValue placeholder={t("common.allTypes")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                <SelectItem value="entrata">{t('dialogs.income')}</SelectItem>
                <SelectItem value="uscita">{t('dialogs.expense')}</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Input 
                placeholder={t("common.searchDescription")} 
                value={searchInput} 
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="w-48"
              />
              <Button variant="outline" size="icon" onClick={handleSearch}><Search className="h-4 w-4" /></Button>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{filteredMovements.length} di {movements.length} movimenti</span>
              <Button variant="outline" onClick={() => setExportDialog(true)} data-testid="btn-export-excel">
                <Download className="h-4 w-4 mr-2" />
                Scarica Excel
              </Button>
              <Button variant="outline" onClick={() => setManualDialog(true)}><Plus className="h-4 w-4 mr-2" />{t('common.manual')}</Button>
              <ExcelUploadHelper
                columns={EXCEL_COLUMNS.movimenti_bancari}
                onUpload={handleUpload}
                uploading={uploading}
                buttonText="Importa Excel"
                title="Struttura File - Movimenti Bancari"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selection toolbar */}
      {selectedIds.size > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
          <CardContent className="py-3">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium">{selectedIds.size} moviment{selectedIds.size === 1 ? 'o' : 'i'} selezionat{selectedIds.size === 1 ? 'o' : 'i'}</span>
              <Button variant="destructive" size="sm" onClick={() => setDeleteMultipleDialog(true)} disabled={deletingBulk} data-testid="bulk-delete-btn">
                {deletingBulk ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Elimina selezionati
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>{t('common.deselect')}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete All button */}
      {movements.length > 0 && selectedIds.size === 0 && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteAllDialog(true)} disabled={deletingBulk} data-testid="delete-all-bank-btn">
            {deletingBulk ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
            Elimina tutti ({movements.length})
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : (
        <>
          <div className="border rounded-xl overflow-hidden shadow-sm bg-card">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-900/50 hover:bg-slate-50 border-b-2">
                    <TableHead className="w-[40px]">
                      <Checkbox checked={sortedMovements.length > 0 && selectedIds.size === sortedMovements.length} onCheckedChange={toggleSelectAll} data-testid="select-all-bank" />
                    </TableHead>
                    <TableHead className="w-[100px]"><SortableHeader field="data" currentSort={sort} onSort={setSort}>Data Cont.</SortableHeader></TableHead>
                    <TableHead className="w-[100px]"><SortableHeader field="data_valuta" currentSort={sort} onSort={setSort}>Data Val.</SortableHeader></TableHead>
                    <TableHead><SortableHeader field="descrizione" currentSort={sort} onSort={setSort}>{t("common.description")}</SortableHeader></TableHead>
                    <TableHead className="w-[120px]"><SortableHeader field="banca_nome" currentSort={sort} onSort={setSort}>{t('dialogs.bank')}</SortableHeader></TableHead>
                    <TableHead className="w-[90px]"><SortableHeader field="tipo" currentSort={sort} onSort={setSort}>{t("common.type")}</SortableHeader></TableHead>
                    <TableHead className="w-[110px] text-right"><SortableHeader field="importo" currentSort={sort} onSort={setSort} align="right">{t("common.amount")}</SortableHeader></TableHead>
                    <TableHead className="w-[110px] text-right"><SortableHeader field="importo_allocato" currentSort={sort} onSort={setSort} align="right">{t('invoices.allocated')}</SortableHeader></TableHead>
                    <TableHead className="w-[100px]"><SortableHeader field="stato_allocazione" currentSort={sort} onSort={setSort}>{t("common.status")}</SortableHeader></TableHead>
                    <TableHead className="w-[80px] text-right">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedMovements.length === 0 ? (
                    <TableRow><TableCell colSpan={10} className="text-center py-16 text-muted-foreground">Nessun movimento bancario trovato</TableCell></TableRow>
                  ) : paginatedMovements.map((row) => (
                    <TableRow key={row.id} className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800 ${selectedIds.has(row.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`} data-testid={`row-${row.id}`}>
                      <TableCell><Checkbox checked={selectedIds.has(row.id)} onCheckedChange={() => toggleSelect(row.id)} /></TableCell>
                      <TableCell className="whitespace-nowrap">{formatDate(row.data)}</TableCell>
                      <TableCell className="whitespace-nowrap">{formatDate(row.data_valuta)}</TableCell>
                      <TableCell className="max-w-[300px] truncate" title={row.descrizione}>{row.descrizione}</TableCell>
                      <TableCell>{row.banca_nome || "-"}</TableCell>
                      <TableCell>{getTipoBadge(row.tipo)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{formatCurrency(row.importo)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{formatCurrency(row.importo_allocato)}</TableCell>
                      <TableCell>{getStatoBadge(row.stato_allocazione)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openAllocDialog(row)} title={t("invoices.allocateMovement")}><Layers className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteSingleDialog(row.id)} title={t("common.delete")}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          
          {/* Pagination */}
          <div className="flex items-center justify-between pb-16">
            <div className="text-sm text-muted-foreground">
              Pagina {currentPage} di {totalPages || 1} ({sortedMovements.length} di {movements.length} movimenti)
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>
                <ChevronLeft className="h-4 w-4 mr-1" />{t('common.previous')}</Button>
              <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>{t('common.nextLabel')}<ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Manual Entry Dialog */}
      <ManualMovementDialog
        open={manualDialog} onClose={() => setManualDialog(false)}
        form={manualForm} setForm={setManualForm}
        formErrors={manualFormErrors} validateField={validateManualField}
        onSubmit={handleManualCreate} saving={savingManual} banche={banche} t={t}
      />

      <AllocationDialog
        open={allocDialog.open}
        onClose={() => { setAllocDialog({ ...allocDialog, open: false }); setEditingAllocationId(null); }}
        allocDialog={allocDialog} allocType={allocType} setAllocType={setAllocType}
        allocForm={allocForm} setAllocForm={setAllocForm}
        editingAllocationId={editingAllocationId} cancelEditAllocation={cancelEditAllocation}
        startEditAllocation={startEditAllocation}
        onAddAllocation={handleAddAllocation} onDeleteAllocation={handleDeleteAllocation}
        calcolaResiduo={calcolaResiduo}
        invoices={invoices} accounts={accounts} costCenters={costCenters}
        allBankMovements={allBankMovements} cardMovements={cardMovements} appartamenti={appartamenti}
        stipendiMesi={stipendiMesi}
        affittiAllocabili={affittiAllocabili}
        pianiScadenzeRicorrenti={pianiScadenzeRicorrenti}
        bolletteManualiAperte={bolletteManualiAperte}
        onNeedGirocontoLists={fetchGirocontoLists}
        formatCurrency={formatCurrency} t={t}
      />

      <DeleteMovementDialogs
        deleteSingleDialog={deleteSingleDialog} setDeleteSingleDialog={setDeleteSingleDialog}
        onDeleteSingle={handleDelete} deletingMovement={deletingMovement}
        deleteMultipleDialog={deleteMultipleDialog} setDeleteMultipleDialog={setDeleteMultipleDialog}
        onBulkDelete={handleBulkDelete} selectedCount={selectedIds.size} deletingBulk={deletingBulk}
        deleteAllDialog={deleteAllDialog} setDeleteAllDialog={setDeleteAllDialog}
        onDeleteAll={handleDeleteAll} totalCount={movements.length} t={t}
      />

      {/* Dialog Export Excel */}
      <Dialog open={exportDialog} onOpenChange={setExportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Scarica Movimenti Bancari</DialogTitle>
            <DialogDescription>Filtra i movimenti da esportare. Lascia vuoto per scaricare tutto.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Da</Label>
                <Input type="date" value={exportForm.data_da} onChange={e => setExportForm({...exportForm, data_da: e.target.value})} data-testid="input-export-da" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">A</Label>
                <Input type="date" value={exportForm.data_a} onChange={e => setExportForm({...exportForm, data_a: e.target.value})} data-testid="input-export-a" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Banca</Label>
              <Select value={exportForm.banca_id} onValueChange={v => setExportForm({...exportForm, banca_id: v})}>
                <SelectTrigger data-testid="select-export-banca">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutte le banche</SelectItem>
                  {banche.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleExport} disabled={exporting} data-testid="btn-conferma-export">
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Scarica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
